import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ForwardedRef,
  Fragment,
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Quiz } from "../../models/Quiz";
import {
  QuizSubmission,
  QuizSubmissionRequest,
} from "../../models/QuizSubmission";

type Props = {
  quiz: Quiz;
  submission?: QuizSubmission;
  isDisabled?: boolean;
};

export type QuizSubmissionHandle = {
  submit: (onSubmit: (submission: QuizSubmissionRequest) => unknown) => void;
};

function QuizSubmissionForm(
  { quiz, submission, isDisabled }: Props,
  ref: ForwardedRef<QuizSubmissionHandle>,
) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { isValid, isSubmitted, errors },
  } = useForm<QuizSubmissionRequest>({
    defaultValues: useMemo(
      () => ({
        submit: true,
        answers: submission?.answers.map(
          (answer) =>
            ({
              questionId: answer.question.id,
              value: answer.value,
            }) ?? [],
        ),
      }),
      [submission],
    ),
  });
  useFieldArray({ control, name: "answers" });

  const currentRadioValues = getValues("answers");

  useImperativeHandle(
    ref,
    () => ({
      submit: (onSubmit) => handleSubmit(onSubmit)(),
    }),
    [handleSubmit],
  );

  const getSolution = useCallback(
    (questionId: string) => {
      return submission?.answers.find((a) => a.question.id === questionId)
        ?.question.solutions[0];
    },
    [submission?.answers],
  );

  return (
    <form>
      <FormControl
        isDisabled={isDisabled}
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <VStack spacing={4}>
          <FormControl
            isInvalid={!isValid && isSubmitted}
            display="flex"
            flexDirection="column"
            gap={6}
          >
            {quiz?.questions.map((question, index) => (
              <div key={question.id}>
                <FormLabel sx={{ fontWeight: "bold" }}>
                  {question.description}
                </FormLabel>
                <RadioGroup
                  isDisabled={isDisabled}
                  colorScheme="primary"
                  defaultValue={currentRadioValues?.[index].value}
                >
                  <VStack alignItems="start" spacing={4}>
                    {Object.entries(question.options).map(
                      ([optionKey, optionValue]) => (
                        <Fragment key={optionKey}>
                          <input
                            hidden
                            {...register(`answers.${index}.questionId`)}
                            value={question.id}
                          />
                          <Radio
                            {...register(`answers.${index}.value`, {
                              required: t("forms.validation.required"),
                            })}
                            value={optionKey}
                          >
                            [{optionKey}] {optionValue}
                          </Radio>
                        </Fragment>
                      ),
                    )}
                  </VStack>
                  {submission && quiz.type === "TASK" && (
                    <Text
                      mt={4}
                      color={
                        getSolution(question.id!) ===
                        getValues(`answers.${index}.value`)
                          ? "green"
                          : "red"
                      }
                    >
                      {t("quiz_submission_page.correct_answer", {
                        param1: getSolution(question.id!),
                      })}
                    </Text>
                  )}
                </RadioGroup>
                <FormErrorMessage>
                  {errors.answers?.[index]?.value?.message}
                </FormErrorMessage>
              </div>
            ))}
          </FormControl>
        </VStack>
      </FormControl>
    </form>
  );
}

export default forwardRef(QuizSubmissionForm) as (
  props: Props & { ref?: Ref<QuizSubmissionHandle> },
) => JSX.Element;
